import { json, type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { Shield } from "lucide-react";
import { getSupabaseWithSessionHeaders } from "../lib/supabase_server";
import Header from "../components/header";
import Footer from "../components/Footer";

export async function loader({ request }: LoaderFunctionArgs) {
  const { headers, supabase, session } = await getSupabaseWithSessionHeaders({ request });
  let userName = "";
  let isAdmin = false;
  if (session) {
    const { data } = await supabase
      .from("Users")
      .select("first_name, admin")
      .eq("id", session.user.id)
      .single();
    userName = data?.first_name ?? "";
    isAdmin = Boolean(data?.admin);
  }
  return json(
    { userId: session?.user?.id ?? "", userName, isAdmin },
    { headers },
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: "Privacy Policy - BulkBuyPlace" },
    { name: "description", content: "BulkBuyPlace privacy policy." },
  ];
};

export default function PrivacyPage() {
  const { userId, userName, isAdmin } = useLoaderData<typeof loader>();
  return (
    <div className="flex flex-col min-h-[100dvh] bg-gray-50">
      <Header userId={userId} userName={userName} isAdmin={isAdmin} />

      <main className="flex-1 pt-16">
        {/* Page header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 py-12 md:py-16 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600 mb-4">
              <Shield className="h-5 w-5" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              Privacy Policy
            </h1>
            <p className="mt-2 text-sm text-gray-400">
              Last updated: February 2026
            </p>
          </div>
        </div>

        {/* Sections */}
        <div className="max-w-3xl mx-auto px-4 py-12 md:py-16">
          <div className="space-y-10">
            {/* 1. Information We Collect */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                1. Information We Collect
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                When you use BulkBuyPlace, we may collect:
              </p>
              <ul className="mt-3 space-y-2.5">
                {[
                  ["Account information", "name, email address, and password when you create an account."],
                  ["Order data", "shipping addresses, order history, and delivery preferences."],
                  ["Store information", "business name, location, product listings, and delivery settings (for suppliers)."],
                  ["Usage data", "pages visited, search queries, and device information to improve the platform."],
                ].map(([label, desc]) => (
                  <li key={label} className="flex items-start gap-2 text-[15px] leading-relaxed text-gray-600">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-orange-400 shrink-0" />
                    <span>
                      <strong className="text-gray-800">{label}</strong> &mdash; {desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 2. How We Use Your Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                2. How We Use Your Information
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                We use your information to:
              </p>
              <ul className="mt-3 space-y-2">
                {[
                  "Operate and improve the platform",
                  "Process orders and connect buyers with suppliers",
                  "Send order updates and account notifications",
                  "Provide customer support",
                  "Prevent fraud and enforce our terms",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[15px] leading-relaxed text-gray-600">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-orange-400 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 3. Information Sharing */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                3. Information Sharing
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                We do not sell your personal information. We share data only as
                needed to operate the platform:
              </p>
              <ul className="mt-3 space-y-2.5">
                {[
                  ["With suppliers", "your name and shipping address are shared with suppliers to fulfill orders."],
                  ["With service providers", "we use Stripe for subscription billing and may use other services for email, hosting, and analytics."],
                  ["As required by law", "if compelled by legal process."],
                ].map(([label, desc]) => (
                  <li key={label} className="flex items-start gap-2 text-[15px] leading-relaxed text-gray-600">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-orange-400 shrink-0" />
                    <span>
                      <strong className="text-gray-800">{label}</strong> &mdash; {desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 4–7 simple sections */}
            {[
              {
                num: 4,
                title: "Data Security",
                content:
                  "We use industry-standard measures to protect your data, including encrypted connections and secure authentication. However, no system is 100% secure, and we cannot guarantee absolute security.",
              },
              {
                num: 5,
                title: "Your Rights",
                content: null,
              },
              {
                num: 6,
                title: "Changes to This Policy",
                content:
                  "We may update this policy from time to time. We will notify you of significant changes via email or a notice on the platform.",
              },
            ].map((s) => (
              <div key={s.num}>
                <h2 className="text-lg font-semibold text-gray-900">
                  {s.num}. {s.title}
                </h2>
                {s.content ? (
                  <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                    {s.content}
                  </p>
                ) : (
                  <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                    You can update your account information at any time from your
                    account settings. To request deletion of your account and
                    data, contact us at{" "}
                    <a
                      href="mailto:bulkbuyplace@gmail.com"
                      className="text-orange-600 hover:underline"
                    >
                      bulkbuyplace@gmail.com
                    </a>
                    .
                  </p>
                )}
              </div>
            ))}

            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                7. Contact
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                Questions about this policy? Contact us at{" "}
                <a
                  href="mailto:bulkbuyplace@gmail.com"
                  className="text-orange-600 hover:underline"
                >
                  bulkbuyplace@gmail.com
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
